import { createContext, useState, useContext, useEffect, useRef } from 'react';
import { AuthService } from '../services/AuthService';
import { useNavigate, useLocation } from 'react-router';
import { clearFavorites } from '../features/lists/favorites/favoritesSlice';
import { clearVisited } from '../features/lists/visited/visitedSlice';
import { useDispatch } from 'react-redux';


const AuthContext = createContext();

// The API answers with one of these codes when the human check (Cloudflare Turnstile) stopped the
// request: `captcha_failed` means try again with a new token, `captcha_unavailable` means Cloudflare
// itself could not be reached.
const CAPTCHA_CODES = ['captcha_failed', 'captcha_unavailable'];
const CAPTCHA_RETRY_MESSAGE = 'Please complete the check that shows you are human, then try again.';

export const AuthProvider = ({ children }) => {

    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);

    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [captchaError, setCaptchaError] = useState('');

    // The sign-up and log-in pages point this at their Turnstile widget, which is what hands out a
    // fresh token for each submitted form.
    const turnstileRef = useRef(null);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();


    /** A token for one request, or null when Turnstile is not configured. Throws if none can be had. */
    const freshCaptchaToken = async () => {
        if (!turnstileRef.current) return null;
        return turnstileRef.current.getFreshToken();
    };

    const withCaptchaToken = (body, token) => (token ? { ...body, turnstile_token: token } : body);

    /** True if the failure was the human check; it is shown by the widget rather than on a field. */
    const showedCaptchaError = (result) => {
        const code = result.data?.code;
        if (!CAPTCHA_CODES.includes(code)) return false;
        setCaptchaError(result.data?.detail || CAPTCHA_RETRY_MESSAGE);
        return true;
    };

    const handleLogin = async (e) => {
        e?.preventDefault?.();
        setCaptchaError('');

        const params = new URLSearchParams(location.search);
        const nextPath = params.get('next') || '/explore';

        let captchaToken;
        try {
            captchaToken = await freshCaptchaToken();
        } catch {
            setCaptchaError(CAPTCHA_RETRY_MESSAGE);
            return { ok: false, captcha: true };
        }

        const login = await AuthService.makeRequest({
            urlExtension: 'token/obtain/',
            method: 'POST',
            body: withCaptchaToken({
                'username': username,
                'password': password
            }, captchaToken)
        });

        if (login.error) {
            if (!showedCaptchaError(login)) {
                setUsernameError(login.data?.detail || "Invalid username or password");
            }
            setPassword('');
            return { ok: false, captcha: CAPTCHA_CODES.includes(login.data?.code) };
        }

        sessionStorage.setItem('tokens', JSON.stringify(login.data));
        setIsLoggedIn(true);
        window.location.href = nextPath;
        return { ok: true };
    };

    const handleLogout = async () => {
        const tokens = JSON.parse(sessionStorage.getItem("tokens"));
        const refresh = tokens?.refresh;

        if (!refresh) {
            sessionStorage.removeItem("tokens");
            window.location.href = '/user/login';
            setIsLoggedIn(false);
            return;
        }

        const logout = await AuthService.makeRequest({
            urlExtension: 'blacklist/',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: { refresh },
        });

        if (logout.error) {
            console.error('[AuthContext] Error logging out');
        }

        dispatch(clearFavorites());
        dispatch(clearVisited());
        sessionStorage.removeItem("tokens");
        navigate('/user/login');
        setIsLoggedIn(false);
    };

    const handleSignup = async (e) => {
        e?.preventDefault?.();
        setCaptchaError('');

        const params = new URLSearchParams(location.search);
        const next = params.get('next') || '/explore';

        let captchaToken;
        try {
            captchaToken = await freshCaptchaToken();
        } catch {
            setCaptchaError(CAPTCHA_RETRY_MESSAGE);
            return;
        }

        const signup = await AuthService.makeRequest({
            urlExtension: 'user/create/',
            method: 'POST',
            body: withCaptchaToken({
                'username': username,
                'password': password,
                'email': email
            }, captchaToken),
        });

        if (signup.error) {
            if (!showedCaptchaError(signup)) {
                setEmailError(signup.data?.email);
                setUsernameError(signup.data?.detail || signup.data?.username);
                setPasswordError(signup.data?.password);
            }
            setPassword('');
            return;
        }

        const currentPath = location.pathname;
        navigate(`${currentPath}?next=${encodeURIComponent(next)}`, { replace: true });

        // Logging in needs a second token: each one may be used once. If that part fails the account
        // still exists, so say so instead of leaving the person looking at a sign-up error.
        const login = await handleLogin(e);
        if (!login.ok && login.captcha) {
            setCaptchaError('Your account was created, but we could not log you in. Please log in.');
        }
    };

    useEffect(() => {
        let isMounted = true;

        const refreshAuth = async () => {
            console.log("[AuthContext] Refreshing tokens...");
            const success = await AuthService.refreshTokens();

            if (isMounted) {
                console.log("[AuthContext] Setting isLoggedIn to", success);
                setIsLoggedIn(success);
                setAuthLoading(false);
            }
        };

        refreshAuth();

        const syncLogout = (event) => {
            if (event.key === 'tokens' && event.newValue === null) {
                setIsLoggedIn(false);
            }
        };

        window.addEventListener("storage", syncLogout);

        return () => {
            isMounted = false;
            window.removeEventListener("storage", syncLogout);
        };
    }, []);

    return (
        <AuthContext.Provider
            value={{
                isLoggedIn,
                handleLogin, handleSignup, handleLogout,
                authLoading,
                username, setUsername,
                email, setEmail,
                password, setPassword,
                showPassword, setShowPassword,
                emailError, usernameError, passwordError,
                captchaError, turnstileRef,
             }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
